import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useVendorAuth } from "../../store/vendorAuthStore";

const API_BASE = "https://xiadot.com/vendor_api";

/* ── helpers ── */
function num(v) { const n = Number(v); return Number.isFinite(n) ? n : 0; }

function toImageUrl(path) {
  if (!path) return "";
  const p = String(path);
  if (p.startsWith("http://") || p.startsWith("https://")) return p;
  return API_BASE + "/" + p.replace(/^\/+/, "");
}

function stockBadge(status, stock) {
  if (status === "out") return {
    label: "Out of stock",
    style: { background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" },
  };
  if (status === "low") return {
    label: `Low (${stock} left)`,
    style: { background: "#fffbeb", color: "#b45309", border: "1px solid #fde68a" },
  };
  return {
    label: "In stock",
    style: { background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0" },
  };
}

function unitLabel(unit, unit_qty) {
  if (!unit) return "piece";
  const q = num(unit_qty);
  if (q > 0 && q !== 1) return q + " " + unit;
  return unit;
}

function escapeCsv(v) {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/* ── icons ── */
const IconInventory = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 7H4a2 2 0 00-2 2v6a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z"/>
    <path d="M16 3H8a2 2 0 00-2 2v2h12V5a2 2 0 00-2-2z"/>
    <path d="M12 17v4M8 17v4M16 17v4"/>
  </svg>
);
const IconSearch = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const IconDownload = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);
const IconEdit = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const IconCheck = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconX = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

/* ── Variant Details Modal ─────────────────────────────────────────── */
function VariantDetailsModal({ product, commission, onClose, onSaveVariant }) {
  const [editingVariant, setEditingVariant] = useState(null);
  const [editStockValue, setEditStockValue] = useState("");
  const [savingVariant, setSavingVariant] = useState(false);
  
  if (!product || !product.product_variants || product.product_variants.length === 0) return null;
  
  const deliveryCharge = num(product.delivery_charge);
  const commissionNum = num(commission);
  
  const startEditVariant = (variant) => {
    setEditingVariant(variant.value);
    setEditStockValue(String(variant.stock_quantity));
  };
  
  const cancelEditVariant = () => {
    setEditingVariant(null);
    setEditStockValue("");
  };
  
  const saveVariantStock = async (variantValue) => {
    const newStock = parseInt(editStockValue, 10);
    if (isNaN(newStock) || newStock < 0) { alert("Enter a valid stock number"); return; }
    
    setSavingVariant(true);
    try {
      if (onSaveVariant) {
        await onSaveVariant(product.id, variantValue, newStock);
      }
      setEditingVariant(null);
      setEditStockValue("");
    } catch (e) {
      alert(e?.message || "Failed to update variant stock");
    } finally {
      setSavingVariant(false);
    }
  };
  
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 9999
    }} onMouseDown={onClose}>
      <div style={{
        background: "#fff", borderRadius: 16, maxWidth: "600px", width: "90%", maxHeight: "80vh", overflow: "auto",
        boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)"
      }} onMouseDown={function(e) { e.stopPropagation(); }}>
        {/* Header */}
        <div style={{ padding: "20px 20px 0", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#111" }}>Variants - {product.name}</h3>
            <span style={{ fontSize: 11, color: "#6b7280", background: "#f3f4f6", padding: "2px 8px", borderRadius: 20 }}>
              {product.product_variants.length} variants
            </span>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, borderRadius: 6 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        
        <div style={{ padding: "20px" }}>
          {/* Customized Values Display */}
          {product.customized_values && product.customized_values.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, color: "#374151", marginBottom: 8 }}>Customized Values:</h4>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {product.customized_values.map((val, idx) => (
                  <span key={idx} style={{
                    padding: "4px 10px", background: "#f3f4f6", 
                    border: "1px solid #d1d5db", borderRadius: 6,
                    fontSize: 12, color: "#374151"
                  }}>
                    {val}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Variants Table */}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                  <th style={{ padding: "10px", textAlign: "left", fontWeight: 600, color: "#374151" }}>Variant</th>
                  <th style={{ padding: "10px", textAlign: "right", fontWeight: 600, color: "#374151" }}>MRP</th>
                  <th style={{ padding: "10px", textAlign: "right", fontWeight: 600, color: "#374151" }}>Offer</th>
                  <th style={{ padding: "10px", textAlign: "right", fontWeight: 600, color: "#374151" }}>Stock</th>
                  <th style={{ padding: "10px", textAlign: "right", fontWeight: 600, color: "#374151" }}>Final Price</th>
                  <th style={{ padding: "10px", textAlign: "center", fontWeight: 600, color: "#374151" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {product.product_variants.map((variant, idx) => {
                  // Offer price is what vendor earns, not base for commission
                  const vendorEarning = variant.offer_price > 0 ? variant.offer_price : variant.mrp_price;
                  const commissionAmt = commissionNum > 0 ? parseFloat(((vendorEarning * commissionNum) / 100).toFixed(2)) : 0;
                  const finalPrice = parseFloat((vendorEarning + commissionAmt + deliveryCharge).toFixed(2));
                  const hasOffer = variant.offer_price > 0 && variant.offer_price < variant.mrp_price;
                  
                  return (
                    <tr key={idx} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "12px 10px", fontWeight: 600, color: "#1f2937" }}>
                        {variant.value}
                      </td>
                      <td style={{ padding: "12px 10px", textAlign: "right", color: hasOffer ? "#9ca3af" : "#374151", textDecoration: hasOffer ? "line-through" : "none" }}>
                        ₹{variant.mrp_price.toFixed(2)}
                      </td>
                      <td style={{ padding: "12px 10px", textAlign: "right", color: hasOffer ? "#059669" : "#9ca3af", fontWeight: hasOffer ? 600 : 400 }}>
                        {hasOffer ? `₹${variant.offer_price.toFixed(2)}` : "-"}
                      </td>
                      <td style={{ padding: "12px 10px", textAlign: "right" }}>
                        {editingVariant === variant.value ? (
                          <input
                            type="number"
                            min="0"
                            value={editStockValue}
                            onChange={e => setEditStockValue(e.target.value)}
                            onKeyDown={e => { 
                              if (e.key === "Enter") saveVariantStock(variant.value); 
                              if (e.key === "Escape") cancelEditVariant(); 
                            }}
                            style={{ 
                              width: "70px", 
                              padding: "4px 6px", 
                              border: "1.5px solid #3b82f6", 
                              borderRadius: 6, 
                              fontSize: 12, 
                              textAlign: "center",
                              outline: "none"
                            }}
                            autoFocus
                          />
                        ) : (
                          <span style={{ color: variant.stock_quantity > 0 ? "#059669" : "#dc2626", fontWeight: 600 }}>
                            {variant.stock_quantity}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "12px 10px", textAlign: "right", color: "#1f2937", fontWeight: 700 }}>
                        ₹{finalPrice.toFixed(2)}
                      </td>
                      <td style={{ padding: "12px 10px", textAlign: "center" }}>
                        {editingVariant === variant.value ? (
                          <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <button
                              onClick={() => saveVariantStock(variant.value)}
                              disabled={savingVariant}
                              style={{
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                width: 24, height: 24, borderRadius: 6, border: "none", cursor: "pointer",
                                background: "#f0fdf4", color: "#15803d"
                              }}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                <polyline points="20 6 9 17 4 12"/>
                              </svg>
                            </button>
                            <button
                              onClick={cancelEditVariant}
                              style={{
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                width: 24, height: 24, borderRadius: 6, border: "none", cursor: "pointer",
                                background: "#fef2f2", color: "#b91c1c"
                              }}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEditVariant(variant)}
                            style={{
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              width: "auto", padding: "4px 8px", gap: 4, borderRadius: 6, border: "none",
                              cursor: "pointer", background: "#f3f4f6", color: "#555",
                              fontSize: 11, fontWeight: 600
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Summary */}
          <div style={{ marginTop: 20, padding: "12px", background: "#f8fafc", borderRadius: 6, border: "1px solid #e2e8f0" }}>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Price Calculation Details:</div>
            <div style={{ fontSize: 12, color: "#374151" }}>
              • Final Price = Vendor Earning + Commission ({commissionNum}%) + Delivery (₹{deliveryCharge.toFixed(2)})<br/>
              • Vendor Earning = Offer Price (if available) else MRP<br/>
              • Total Variants: {product.product_variants.length} | Total Stock: {product.product_variants.reduce((sum, v) => sum + v.stock_quantity, 0)}
            </div>
          </div>
          
          {/* Cancel Button */}
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid #e5e7eb", textAlign: "center" }}>
            <button 
              onClick={onClose}
              style={{ 
                background: "#6b7280", 
                color: "#fff", 
                padding: "10px 24px", 
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 500,
                border: "none",
                cursor: "pointer",
                minWidth: "100px"
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── styles ── */
const S = {
  page:         { padding: 24, background: "#f0f2f5", minHeight: "100%" },
  head:         { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 20, flexWrap: "wrap" },
  headLeft:     { display: "flex", alignItems: "center", gap: 14 },
  headRight:    { display: "flex", alignItems: "center", gap: 10 },
  iconBox:      { width: 44, height: 44, background: "#1a1f2e", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  title:        { margin: 0, fontSize: 22, fontWeight: 800, color: "#111", lineHeight: 1.2 },
  sub:          { marginTop: 3, color: "#888", fontSize: 13 },
  stats:        { display: "flex", gap: 14, marginBottom: 20, flexWrap: "wrap" },
  statCard:     { background: "#fff", borderRadius: 14, padding: "14px 20px", display: "flex", alignItems: "center", gap: 14, border: "1px solid #eee", flex: 1, minWidth: 130 },
  statLabel:    { fontSize: 12, color: "#888", fontWeight: 500, marginBottom: 4 },
  statVal:      { fontSize: 22, fontWeight: 800, color: "#111", lineHeight: 1 },
  toolbar:      { display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" },
  searchWrap:   { position: "relative", display: "flex", alignItems: "center", flex: 1, minWidth: 200 },
  searchIcon:   { position: "absolute", left: 12, pointerEvents: "none", display: "flex", alignItems: "center" },
  searchInput:  { width: "100%", padding: "10px 14px 10px 36px", border: "1.5px solid #e2e5ea", borderRadius: 12, outline: "none", background: "#fff", fontSize: 13.5, color: "#222", boxSizing: "border-box" },
  filterSelect: { padding: "10px 12px", border: "1.5px solid #e2e5ea", borderRadius: 12, outline: "none", background: "#fff", fontSize: 13.5, color: "#444", cursor: "pointer" },
  tableCard:    { background: "#fff", border: "1px solid #eee", borderRadius: 16, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" },
  tableWrap:    { overflowX: "auto" },
  table:        { width: "100%", borderCollapse: "collapse", minWidth: 800 },
  theadRow:     { background: "#f7f8fa" },
  th:           { textAlign: "left", fontSize: 12, color: "#555", padding: "13px 16px", borderBottom: "1px solid #eeeff1", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap" },
  td:           { padding: "12px 16px", borderBottom: "1px solid #f3f4f6", fontSize: 13.5, verticalAlign: "middle" },
  tdSno:        { padding: "12px 16px", borderBottom: "1px solid #f3f4f6", fontSize: 12.5, verticalAlign: "middle", color: "#aaa", fontWeight: 600 },
  badge:        { display: "inline-flex", alignItems: "center", padding: "4px 10px", borderRadius: 999, fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap" },
  editInput:    { width: 70, padding: "5px 8px", border: "1.5px solid #3b82f6", borderRadius: 8, fontSize: 13, outline: "none", textAlign: "center" },
  editBtn:      { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 7, border: "none", cursor: "pointer", fontSize: 12 },
  footer:       { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderTop: "1px solid #f3f4f6", fontSize: 13, color: "#888" },
  footerStrong: { color: "#333", fontWeight: 700 },
  alert:        { padding: "12px 16px", borderRadius: 12, background: "#ffe9e9", border: "1px solid #ffc8c8", color: "#a40000", marginBottom: 16, fontSize: 13.5 },
  emptyTd:      { padding: "48px 16px", textAlign: "center" },
  emptyText:    { margin: 0, fontSize: 14, color: "#999", fontWeight: 500, marginTop: 10 },
  refreshBtn:   { padding: "9px 18px", background: "#fff", border: "1.5px solid #e2e5ea", borderRadius: 12, fontSize: 13.5, fontWeight: 700, cursor: "pointer", color: "#444" },
  downloadBtn:  { display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 18px", background: "#1a1f2e", color: "#fff", border: "none", borderRadius: 12, fontSize: 13.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" },
};

export default function Inventory() {
  const { vendor, token } = useVendorAuth();
  const vendorId = vendor?.id || vendor?._id || "";

  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [err,      setErr]      = useState("");
  const [q,        setQ]        = useState("");
  const [filter,   setFilter]   = useState("all");
  const [editId,   setEditId]   = useState(null);
  const [editVal,  setEditVal]  = useState("");
  const [saving,   setSaving]   = useState(false);
  const [variantOpen, setVariantOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [categoryCommissions, setCategoryCommissions] = useState({});

  /* ── fetch category commissions ─────────────────────────────────────────── */
  const fetchCategoryCommissions = useCallback(async () => {
    try {
      const urls = [
        `${API_BASE}/admin/main_categories/list.php`,
        `${API_BASE}/main_categories/list.php`,
        `${API_BASE}/vendor/main_categories/list.php`,
      ];
      let data = null;
      for (let i = 0; i < urls.length; i++) {
        try {
          const r = await fetch(urls[i], { headers: { Authorization: `Bearer ${token}` } });
          if (!r.ok) continue;
          data = await r.json().catch(() => null);
          if (data) break;
        } catch (e) {}
      }
      if (data) {
        const list = data.categories || data.main_categories || data.data || [];
        const map = {};
        (Array.isArray(list) ? list : []).forEach(c => {
          const cid = String(c.id != null ? c.id : c.category_id != null ? c.category_id : "");
          const comm = c.commission != null ? Number(c.commission)
            : c.commission_percent != null ? Number(c.commission_percent)
            : c.commission_percentage != null ? Number(c.commission_percentage)
            : null;
          if (cid && comm != null) map[cid] = comm;
        });
        setCategoryCommissions(map);
      }
    } catch (e) {}
  }, [token]);

  /* ── fetch ───────────────────────────────────────────────────────────────── */
  const load = useCallback(async () => {
    if (!vendorId) { setProducts([]); setLoading(false); return; }
    setLoading(true);
    try {
      const res  = await fetch(
        `${API_BASE}/vendor/products/inventory.php?vendor_id=${vendorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.success && Array.isArray(data.products)) {
        // Fetch variant details for each product
        const productsWithVariants = await Promise.all(data.products.map(async function(p) {
          let productVariants = [];
          let customizedValues = [];
          
          try {
            const detRes = await fetch(`${API_BASE}/user/get-product-details.php?id=${p.id}`);
            const detData = await detRes.json();
            if (detData.success) {
              if (Array.isArray(detData.data.product_variants)) {
                productVariants = detData.data.product_variants.map(v => ({
                  value: v.value || "",
                  mrp_price: num(v.mrp_price),
                  offer_price: num(v.offer_price),
                  stock_quantity: num(v.stock_quantity)
                }));
              }
              if (Array.isArray(detData.data.customized_values)) {
                customizedValues = detData.data.customized_values;
              }
            }
          } catch (e) {}
          
          return {
            ...p,
            product_variants: productVariants,
            customized_values: customizedValues
          };
        }));
        setProducts(productsWithVariants);
      } else {
        setErr(data.message || "Failed to load inventory");
        setProducts([]);
      }
    } catch (e) {
      setErr(e?.message || "Network error");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [vendorId, token]);

  useEffect(() => { load(); fetchCategoryCommissions(); }, [load, fetchCategoryCommissions]);

  /* ── stats ───────────────────────────────────────────────────────────────── */
  const stats = useMemo(() => {
    let inStock = 0, low = 0, out = 0;
    
    products.forEach(p => {
      let stockStatus;
      if (!p.product_variants || p.product_variants.length === 0) {
        stockStatus = p.stock_status;
      } else {
        const totalStock = p.product_variants.reduce((sum, v) => sum + v.stock_quantity, 0);
        if (totalStock <= 0) {
          stockStatus = "out";
        } else if (totalStock <= 5) {
          stockStatus = "low";
        } else {
          stockStatus = "ok";
        }
      }
      
      if (stockStatus === "ok") inStock++;
      else if (stockStatus === "low") low++;
      else if (stockStatus === "out") out++;
    });
    
    return {
      total: products.length,
      inStock: inStock,
      low: low,
      out: out,
      totalSold: products.reduce((a, p) => a + num(p.sold_qty), 0),
    };
  }, [products]);

  /* ── filtered list ───────────────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return products.filter(p => {
      if (s && !(p.name || "").toLowerCase().includes(s)) return false;
      
      if (filter !== "all") {
        let stockStatus;
        if (!p.product_variants || p.product_variants.length === 0) {
          stockStatus = p.stock_status;
        } else {
          const totalStock = p.product_variants.reduce((sum, v) => sum + v.stock_quantity, 0);
          if (totalStock <= 0) {
            stockStatus = "out";
          } else if (totalStock <= 5) {
            stockStatus = "low";
          } else {
            stockStatus = "ok";
          }
        }
        if (stockStatus !== filter) return false;
      }
      return true;
    });
  }, [products, q, filter]);

  /* ── CSV download ────────────────────────────────────────────────────────── */
  const downloadCSV = () => {
    const headers = ["S.NO", "PRODUCT", "UNIT", "PRICE", "OFFER_PRICE", "STOCK", "SOLD", "STATUS"];
    const lines   = [headers.join(",")];

    filtered.forEach((p, idx) => {
      const uLabel = unitLabel(p.unit, p.unit_qty) || p.unit || "piece";
      const status = p.stock_status === "out" ? "Out of stock"
                   : p.stock_status === "low" ? `Low (${num(p.stock)} left)`
                   : "In stock";
      lines.push([
        idx + 1,
        escapeCsv(p.name || ""),
        escapeCsv(uLabel),
        `₹${num(p.price).toFixed(2)}`,
        num(p.offer_price) > 0 ? `₹${num(p.offer_price).toFixed(2)}` : "",
        num(p.stock),
        num(p.sold_qty),
        escapeCsv(status),
      ].join(","));
    });

    const csv  = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `inventory_vendor${vendorId}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  /* ── open variant details ───────────────────────────────────────────────── */
  const openVariantDetails = (product) => {
    setSelectedProduct(product);
    setVariantOpen(true);
  };

  /* ── inline stock edit ───────────────────────────────────────────────────── */
  const startEdit  = (p) => { setEditId(p.id); setEditVal(String(p.stock)); };
  const cancelEdit = ()  => { setEditId(null); setEditVal(""); };

  const saveEdit = async (productId) => {
    const newStock = parseInt(editVal, 10);
    if (isNaN(newStock) || newStock < 0) { alert("Enter a valid stock number"); return; }
    setSaving(true);
    try {
      const res  = await fetch(
        `${API_BASE}/vendor/products/inventory.php?vendor_id=${vendorId}`,
        {
          method:  "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body:    JSON.stringify({ product_id: productId, stock: newStock }),
        }
      );
      const data = await res.json();
      if (!res.ok || data.success === false) { alert(data.message || "Update failed"); return; }
      setProducts(prev => prev.map(p => {
        if (p.id !== productId) return p;
        const status = newStock <= 0 ? "out" : newStock <= 5 ? "low" : "ok";
        return { ...p, stock: newStock, stock_status: status };
      }));
      setEditId(null);
    } catch (e) {
      alert(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveVariantStock = useCallback(async (productId, variantValue, newStock) => {
  try {
    const res = await fetch(
      `${API_BASE}/vendor/products/update-variant-stock.php`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ 
          vendor_id: vendorId,      // ← ADD THIS
          product_id: productId, 
          variant_value: variantValue, 
          stock: newStock 
        }),
      }
    );

    const rawText = await res.text();
    let data;
    try {
      data = JSON.parse(rawText);
    } catch (parseErr) {
      console.error("Server returned non-JSON:", rawText);
      throw new Error("Server error: " + rawText.slice(0, 200));
    }

    if (!res.ok || data.success === false) {
      throw new Error(data.message || "Failed to update variant stock");
    }

    setProducts(prev => prev.map(p => {
      if (p.id !== productId) return p;
      const updatedVariants = p.product_variants.map(v =>
        v.value === variantValue ? { ...v, stock_quantity: newStock } : v
      );
      return { ...p, product_variants: updatedVariants };
    }));

    setSelectedProduct(prev => {
      if (!prev || prev.id !== productId) return prev;
      const updatedVariants = prev.product_variants.map(v =>
        v.value === variantValue ? { ...v, stock_quantity: newStock } : v
      );
      return { ...prev, product_variants: updatedVariants };
    });

  } catch (e) {
    console.error("Save variant stock error:", e);
    throw e;
  }
}, [token, vendorId, setProducts, setSelectedProduct]);

  /* ── render ──────────────────────────────────────────────────────────────── */
  return (
    <div style={S.page}>

      {/* Header */}
      <div style={S.head}>
        <div style={S.headLeft}>
          <div style={S.iconBox}><IconInventory /></div>
          <div>
            <h2 style={S.title}>Inventory</h2>
            <div style={S.sub}>
              {loading ? "Loading…" : `${products.length} products · vendor #${vendorId}`}
            </div>
          </div>
        </div>

        {/* ✅ Download + Refresh buttons */}
        <div style={S.headRight}>
          <button style={S.downloadBtn} onClick={downloadCSV} disabled={loading || products.length === 0}>
            <IconDownload />
            Download CSV
          </button>
          <button style={S.refreshBtn} onClick={load}>
            Refresh
          </button>
        </div>
      </div>

      {err ? <div style={S.alert}>{err}</div> : null}

      {/* Stats */}
      <div style={S.stats}>
        <div style={S.statCard}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"><path d="M20 7H4a2 2 0 00-2 2v6a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z"/></svg>
          </div>
          <div>
            <div style={S.statLabel}>Total products</div>
            <div style={S.statVal}>{stats.total}</div>
          </div>
        </div>
        <div style={S.statCard}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <div>
            <div style={S.statLabel}>In stock</div>
            <div style={{ ...S.statVal, color: "#16a34a" }}>{stats.inStock}</div>
          </div>
        </div>
        <div style={S.statCard}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: "#fffbeb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <div>
            <div style={S.statLabel}>Low stock</div>
            <div style={{ ...S.statVal, color: "#d97706" }}>{stats.low}</div>
          </div>
        </div>
        <div style={S.statCard}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
          </div>
          <div>
            <div style={S.statLabel}>Out of stock</div>
            <div style={{ ...S.statVal, color: "#dc2626" }}>{stats.out}</div>
          </div>
        </div>
        <div style={S.statCard}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2" strokeLinecap="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
          </div>
          <div>
            <div style={S.statLabel}>Total sold</div>
            <div style={{ ...S.statVal, color: "#4f46e5" }}>{stats.totalSold}</div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div style={S.toolbar}>
        <div style={S.searchWrap}>
          <span style={S.searchIcon}><IconSearch /></span>
          <input
            style={S.searchInput}
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search products…"
          />
        </div>
        <select style={S.filterSelect} value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="all">All status</option>
          <option value="ok">In stock</option>
          <option value="low">Low stock</option>
          <option value="out">Out of stock</option>
        </select>
      </div>

      {/* Table */}
      <div style={S.tableCard}>
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr style={S.theadRow}>
                <th style={S.th}>S.No</th>
                <th style={S.th}>Product</th>
                <th style={S.th}>Unit</th>
                <th style={S.th}>Price</th>
                <th style={S.th}>Stock</th>
                <th style={S.th}>Sold</th>
                <th style={S.th}>Status</th>
                <th style={{ ...S.th, textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} style={S.emptyTd}>
                    <p style={S.emptyText}>Loading inventory…</p>
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} style={S.emptyTd}>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#ddd" strokeWidth="1.5" strokeLinecap="round">
                      <path d="M20 7H4a2 2 0 00-2 2v6a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z"/>
                    </svg>
                    <p style={S.emptyText}>{q ? "No products match your search" : "No products found"}</p>
                  </td>
                </tr>
              )}
              {!loading && filtered.map((p, idx) => {
                // Calculate correct stock status for both simple and variant products
                let stockStatus, stockQuantity;
                if (!p.product_variants || p.product_variants.length === 0) {
                  // Simple product
                  stockStatus = p.stock_status;
                  stockQuantity = p.stock;
                } else {
                  // Variant product - calculate based on total stock
                  const totalStock = p.product_variants.reduce((sum, v) => sum + v.stock_quantity, 0);
                  stockQuantity = totalStock;
                  if (totalStock <= 0) {
                    stockStatus = "out";
                  } else if (totalStock <= 5) {
                    stockStatus = "low";
                  } else {
                    stockStatus = "ok";
                  }
                }
                
                const badge     = stockBadge(stockStatus, stockQuantity);
                const uLabel    = unitLabel(p.unit, p.unit_qty);
                const isEditing = editId === p.id;

                return (
                  <tr key={p.id}
                    style={{ background: "transparent" }}
                    onMouseEnter={e => (e.currentTarget.style.background = "#fafbfc")}
                    onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                  >
                    <td style={S.tdSno}>{idx + 1}</td>

                    {/* Product */}
                    <td style={S.td}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {p.image ? (
                          <img
                            src={toImageUrl(p.image)}
                            alt={p.name}
                            style={{ width: 36, height: 36, borderRadius: 8, objectFit: "cover", border: "1px solid #eee", flexShrink: 0 }}
                            onError={e => { e.currentTarget.style.display = "none"; }}
                          />
                        ) : (
                          <div style={{ width: 36, height: 36, borderRadius: 8, background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 14, fontWeight: 700, color: "#aaa" }}>
                            {(p.name || "P").charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div style={{ fontWeight: 700, color: "#111", fontSize: 13.5 }}>{p.name || "-"}</div>
                          {p.description && (
                            <div style={{ fontSize: 12, color: "#888", marginTop: 1 }}>
                              {p.description.slice(0, 40)}{p.description.length > 40 ? "…" : ""}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Unit */}
                    <td style={S.td}>
                      {uLabel ? (
                        <span style={{ padding: "2px 8px", borderRadius: 6, background: "#f0f9ff", color: "#0369a1", fontSize: 11.5, fontWeight: 600 }}>
                          {uLabel}
                        </span>
                      ) : (
                        <span style={{ color: "#ccc", fontSize: 12 }}>—</span>
                      )}
                    </td>

                    {/* Price */}
                    <td style={S.td}>
                      {(!p.product_variants || p.product_variants.length === 0) ? (
                        <>
                          <div style={{ fontWeight: 700, fontSize: 13.5 }}>₹{num(p.price).toFixed(2)}</div>
                          {num(p.offer_price) > 0 && (
                            <div style={{ fontSize: 12, color: "#16a34a" }}>Offer: ₹{num(p.offer_price).toFixed(2)}</div>
                          )}
                        </>
                      ) : (
                        <span style={{ color: "#9ca3af", fontSize: 12, fontStyle: "italic" }}>View variants</span>
                      )}
                    </td>

                    {/* Stock */}
                    <td style={S.td}>
                      {(!p.product_variants || p.product_variants.length === 0) ? (
                        <span style={{ fontWeight: 800, fontSize: 15, color: stockStatus === "out" ? "#dc2626" : stockStatus === "low" ? "#d97706" : "#111" }}>
                          {num(stockQuantity)}
                        </span>
                      ) : (
                        <span style={{ color: "#9ca3af", fontSize: 12, fontStyle: "italic" }}>
                          {num(stockQuantity)} total
                        </span>
                      )}
                    </td>

                    {/* Sold */}
                    <td style={S.td}>
                      <span style={{ fontWeight: 700, color: "#4f46e5", fontSize: 13.5 }}>
                        {num(p.sold_qty)}
                      </span>
                    </td>

                    {/* Status */}
                    <td style={S.td}>
                      <span style={{ ...S.badge, ...badge.style }}>{badge.label}</span>
                    </td>

                    {/* Actions */}
                    <td style={{ ...S.td, textAlign: "center" }}>
                      {isEditing ? (
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                          <input
                            style={S.editInput}
                            type="number"
                            min="0"
                            value={editVal}
                            onChange={e => setEditVal(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") saveEdit(p.id); if (e.key === "Escape") cancelEdit(); }}
                            autoFocus
                          />
                          <button
                            style={{ ...S.editBtn, background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0" }}
                            onClick={() => saveEdit(p.id)}
                            disabled={saving}
                          >
                            <IconCheck />
                          </button>
                          <button
                            style={{ ...S.editBtn, background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" }}
                            onClick={cancelEdit}
                          >
                            <IconX />
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                          {/* Show Edit Stock button only for products without variants */}
                          {(!p.product_variants || p.product_variants.length === 0) && (
                            <button
                              style={{ ...S.editBtn, background: "#f3f4f6", color: "#555", border: "1px solid #e5e7eb", width: "auto", padding: "5px 12px", gap: 5, display: "inline-flex", alignItems: "center" }}
                              onClick={() => startEdit(p)}
                            >
                              <IconEdit /> Edit
                            </button>
                          )}
                          
                          {/* Show View More button only for products with variants */}
                          {p.product_variants && p.product_variants.length > 0 && (
                            <button
                              style={{ ...S.editBtn, background: "#f0f9ff", color: "#0369a1", border: "1px solid #bae6fd", width: "auto", padding: "5px 12px", gap: 5, display: "inline-flex", alignItems: "center" }}
                              onClick={() => openVariantDetails(p)}
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                <circle cx="11" cy="11" r="8" />
                                <path d="m21 21-4.35-4.35" />
                                <line x1="11" y1="8" x2="11" y2="14" />
                                <line x1="8" y1="11" x2="14" y2="11" />
                              </svg>
                              View More
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={S.footer}>
          <span>
            Showing <strong style={S.footerStrong}>{filtered.length}</strong> of{" "}
            <strong style={S.footerStrong}>{products.length}</strong> products
          </span>
          {loading && <span style={{ color: "#aaa" }}>Loading…</span>}
        </div>
      </div>
      
      {/* Variant Details Modal */}
      {variantOpen && selectedProduct && (
        <VariantDetailsModal 
          product={selectedProduct} 
          commission={categoryCommissions[selectedProduct.main_category_id]}
          onClose={() => { setVariantOpen(false); setSelectedProduct(null); }} 
          onSaveVariant={saveVariantStock}
        />
      )}
    </div>
  );
}